import React, { useState } from "react";
import { TOKENS } from "../tokens";
import { useClock, fmtTime } from "../hooks/useClock";
import { useIsMobile } from "../hooks/useIsMobile";
import { useApp } from "../context/AppContext";
import { OverviewScreen } from "./OverviewScreen";
import { TeamScreen } from "./TeamScreen";
import { EmployeeDetailScreen } from "./EmployeeDetailScreen";
import { ReimbursementApprovals } from "./ReimbursementApprovals";
import { ManagerLeaveScreen } from "./ManagerLeaveScreen";
import { ManagerReportsCalendarScreen } from "./ManagerReportsCalendarScreen";
import { ManagerLunchScreen } from "./ManagerLunchScreen";
import { ManagerIncidentScreen } from "./ManagerIncidentScreen";
import { VoucherPrinterScreen } from "./VoucherPrinterScreen";
import { ManagerPayrollScreen } from "./ManagerPayrollScreen";
import { ProjectsScreen } from "./ProjectsScreen";
import { TasksScreen } from "./TasksScreen";
import { ManagerDeliveryScreen } from "./ManagerDeliveryScreen";
import { ManagerAllocationsScreen } from "./ManagerAllocationsScreen";
import api from "../api/client";
import { BarChart3, Users, CalendarDays, Wallet, Calendar, LogOut, Clock, ChevronLeft, FileText, KeyRound, Bell, CheckCheck, Coffee, AlertTriangle, Printer, Calculator, Menu, X, Truck, ChevronDown, ChevronRight } from "lucide-react";

const TABS = [
  { id: "overview",      label: "Analytics",               Icon: BarChart3, mobileLabel: "Analytics" },
  { id: "team",          label: "Team",                    Icon: Users },
  { id: "reports",       label: "Daily Reports",           Icon: CalendarDays, mobileLabel: "Reports" },
  { id: "reimbursement", label: "Reimbursements",            Icon: Wallet },
  { id: "voucherPrinter", label: "Excel Voucher Printer",   Icon: Printer, mobileLabel: "Vouchers" },
  { id: "leaves",        label: "Leave Applications",      Icon: FileText, mobileLabel: "Leaves" },
  { id: "lunch",         label: "Lunch Requests",          Icon: Coffee, mobileLabel: "Lunch" },
  { id: "incidents",     label: "Incident Register",       Icon: AlertTriangle, mobileLabel: "Incidents" },
  { id: "payroll",       label: "Payroll Processing",      Icon: Calculator, mobileLabel: "Payroll" },
  { id: "projects",      label: "Project Management",      Icon: Calendar, mobileLabel: "Projects" },
  { id: "pm_tasks",      label: "Project Tasks",           Icon: CheckCheck, mobileLabel: "PM Tasks" },
  { id: "deliveries",    label: "Deliveries",              Icon: Truck, mobileLabel: "Deliveries" },
  { id: "team_alloc",    label: "Allocations & Timesheets",Icon: Users, mobileLabel: "Timesheets" },
];

const SIDEBAR_STRUCTURE = [
  { type: "tab", id: "overview" },
  {
    type: "group",
    id: "group_team",
    label: "Team",
    Icon: Users,
    children: [
      { type: "tab", id: "team", overrideLabel: "Team Directory" },
      { type: "tab", id: "reports" },
      { type: "tab", id: "reimbursement" },
      { type: "tab", id: "leaves" },
      { type: "tab", id: "lunch" },
      { type: "tab", id: "incidents" },
      { type: "tab", id: "payroll" }
    ]
  },
  {
    type: "group",
    id: "group_projects",
    label: "Projects",
    Icon: Calendar,
    children: [
      { type: "tab", id: "projects" },
      { type: "tab", id: "pm_tasks" },
      { type: "tab", id: "deliveries" },
      { type: "tab", id: "team_alloc" }
    ]
  },
  { type: "tab", id: "voucherPrinter" }
];

export function ManagerApp() {
  const [tab, setTab] = useState("overview");
  const [selectedEmpId, setSelectedEmpId] = useState(null);
  const { logout, notifications, unreadNotifCount, markNotificationsRead } = useApp();
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const now = useClock();
  const isMobile = useIsMobile();
  const [expandedGroups, setExpandedGroups] = useState({ group_team: true, group_projects: true });

  const toggleGroup = (groupId) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const [managerPic, setManagerPic] = useState(() => localStorage.getItem("manager_profile_pic") || "");
  const fileInputRef = React.useRef();

  // Password change state
  const [isPwModalOpen, setIsPwModalOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState("");

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwError("");
    if (newPw !== confirmPw) {
      setPwError("New passwords do not match.");
      return;
    }
    if (newPw.length < 6) {
      setPwError("New password must be at least 6 characters long.");
      return;
    }
    setPwLoading(true);
    try {
      await api.post("/auth/change-password", {
        currentPassword: currentPw,
        newPassword: newPw,
      });
      alert("Manager password updated successfully!");
      setIsPwModalOpen(false);
      setCurrentPw("");
      setNewPw("");
      setConfirmPw("");
    } catch (err) {
      setPwError(err.response?.data?.error || "Failed to update password");
    } finally {
      setPwLoading(false);
    }
  };

  const handlePicChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      localStorage.setItem("manager_profile_pic", ev.target.result);
      setManagerPic(ev.target.result);
    };
    reader.readAsDataURL(file);
  };

  const handleTabChange = (newTab) => {
    setSelectedEmpId(null);
    setTab(newTab);
  };

  // ── MOBILE LAYOUT ──────────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", height: "100vh", width: "100vw", background: TOKENS.bgPage, overflow: "hidden" }}>
        {/* Mobile Top Header */}
        <div style={{
          background: `linear-gradient(135deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
          padding: "12px 16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexShrink: 0,
          boxShadow: "0 2px 12px rgba(10,25,50,0.2)",
        }}>
          {/* Back button or Logo */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {selectedEmpId ? (
              <button onClick={() => setSelectedEmpId(null)} style={{
                background: "rgba(255,255,255,0.1)", border: "none", borderRadius: 8,
                padding: "6px 10px", display: "flex", alignItems: "center", gap: 4,
                color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer",
              }}>
                <ChevronLeft size={16} /> Back
              </button>
            ) : (
              <>
                <img src="/goldpe-logo.png" alt="Logo" style={{ width: 30, height: 30, borderRadius: 8, background: "#fff", padding: 2 }} />
                <div>
                  <div style={{ fontFamily: "Fraunces, serif", fontSize: 15, fontWeight: 700, color: "#fff", lineHeight: 1 }}>GoldPE WIBIL</div>
                  <div style={{ fontSize: 9, color: TOKENS.goldLight, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>Manager Portal</div>
                </div>
              </>
            )}
          </div>

          {/* Right: clock + notif + key + sign out */}
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 11, color: "#C7D2E3", display: "flex", alignItems: "center", gap: 4 }}>
              <Clock size={12} color={TOKENS.gold} />
              <span style={{ fontWeight: 700, color: TOKENS.gold }}>{fmtTime(now)}</span>
            </div>

            {/* Notification Bell */}
            <button
              onClick={() => {
                setIsNotifOpen(true);
                markNotificationsRead();
              }}
              title="Notifications"
              style={{
                background: "rgba(245,184,0,0.15)", border: `1px solid ${TOKENS.gold}50`,
                borderRadius: 8, padding: "6px 8px", display: "flex", alignItems: "center", justifyContent: "center",
                color: TOKENS.gold, cursor: "pointer", position: "relative"
              }}
            >
              <Bell size={13} />
              {unreadNotifCount > 0 && (
                <span style={{
                  position: "absolute", top: -4, right: -4, background: "#ef4444", color: "#fff",
                  fontSize: 9, fontWeight: 800, borderRadius: 10, padding: "1px 5px", minWidth: 16, textAlign: "center"
                }}>
                  {unreadNotifCount}
                </span>
              )}
            </button>

            <button onClick={() => setIsPwModalOpen(true)} title="Change Password" style={{
              background: "rgba(245,184,0,0.15)", border: `1px solid ${TOKENS.gold}50`,
              borderRadius: 8, padding: "6px 8px", display: "flex", alignItems: "center", justifyContent: "center",
              color: TOKENS.gold, cursor: "pointer",
            }}>
              <KeyRound size={13} />
            </button>
            <button onClick={logout} style={{
              background: "rgba(232,137,111,0.15)", border: "1px solid rgba(232,137,111,0.3)",
              borderRadius: 8, padding: "6px 10px", display: "flex", alignItems: "center", gap: 4,
              color: "#E8896F", fontSize: 12, fontWeight: 700, cursor: "pointer",
            }}>
              <LogOut size={13} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 14px 80px", WebkitOverflowScrolling: "touch" }}>
          <div key={selectedEmpId || tab} className="screen-enter">
            {selectedEmpId ? (
              <EmployeeDetailScreen empId={selectedEmpId} onBack={() => setSelectedEmpId(null)} />
            ) : (
              <>
                {tab === "overview"      && <OverviewScreen onSelectEmp={(id) => setSelectedEmpId(id)} onNavigate={handleTabChange} />}
                {tab === "team"          && <TeamScreen onSelectEmp={(id) => setSelectedEmpId(id)} />}
                {tab === "reports"       && <ManagerReportsCalendarScreen onSelectEmp={(id) => setSelectedEmpId(id)} />}
                {tab === "reimbursement" && <ReimbursementApprovals />}
                {tab === "leaves"        && <ManagerLeaveScreen />}
                { tab === "lunch"         && <ManagerLunchScreen />}
                {tab === "incidents"     && <ManagerIncidentScreen />}
                {tab === "payroll"       && <ManagerPayrollScreen />}
                {tab === "projects"      && <ProjectsScreen onNavigate={handleTabChange} />}
                {tab === "pm_tasks"      && <TasksScreen />}
                {tab === "deliveries"    && <ManagerDeliveryScreen />}
                {tab === "team_alloc"    && <ManagerAllocationsScreen />}
              </>
            )}
          </div>
        </div>

        {/* Mobile Bottom Navigation */}
        <div style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: "#fff",
          borderTop: `1px solid ${TOKENS.border}`,
          display: "flex",
          justifyContent: "space-between",
          boxShadow: "0 -4px 20px rgba(10,25,50,0.10)",
          zIndex: 100,
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
        }}>
          {TABS.slice(0, 4).map((t) => {
            const { id, label, mobileLabel, Icon, action } = t;
            const displayLabel = mobileLabel || label;
            const active = tab === id && !selectedEmpId;
            return (
              <button
                key={id}
                onClick={() => {
                  if (action) action();
                  else handleTabChange(id);
                  setIsMobileMenuOpen(false);
                }}
                style={{
                  flex: 1, display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  padding: "10px 4px 8px",
                  border: "none", background: "none", cursor: "pointer",
                  color: active ? TOKENS.navyDeep : TOKENS.muted,
                  position: "relative",
                }}
              >
                {active && (
                  <div style={{
                    position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
                    width: 28, height: 3, borderRadius: 2, background: TOKENS.navyDeep,
                  }} />
                )}
                <Icon size={20} color={active ? TOKENS.navyDeep : TOKENS.muted} />
                <span style={{ fontSize: 10, fontWeight: active ? 700 : 500, marginTop: 3, letterSpacing: 0.2, whiteSpace: "nowrap" }}>
                  {displayLabel}
                </span>
              </button>
            );
          })}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            style={{
              flex: 1, display: "flex", flexDirection: "column",
              alignItems: "center", justifyContent: "center",
              padding: "10px 4px 8px",
              border: "none", background: "none", cursor: "pointer",
              color: isMobileMenuOpen ? TOKENS.navyDeep : TOKENS.muted,
              position: "relative",
            }}
          >
            {isMobileMenuOpen && (
              <div style={{
                position: "absolute", top: 0, left: "50%", transform: "translateX(-50%)",
                width: 28, height: 3, borderRadius: 2, background: TOKENS.navyDeep,
              }} />
            )}
            <Menu size={20} color={isMobileMenuOpen ? TOKENS.navyDeep : TOKENS.muted} />
            <span style={{ fontSize: 10, fontWeight: isMobileMenuOpen ? 700 : 500, marginTop: 3, letterSpacing: 0.2, whiteSpace: "nowrap" }}>
              Menu
            </span>
          </button>
        </div>

        {/* Mobile Full Menu Modal */}
        {isMobileMenuOpen && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 60,
            background: "#fff", zIndex: 99, display: "flex", flexDirection: "column",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)"
          }}>
            <div style={{ padding: "20px 16px", borderBottom: `1px solid ${TOKENS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", background: TOKENS.cream }}>
              <h3 style={{ margin: 0, fontSize: 18, color: TOKENS.navyDeep, fontFamily: "Fraunces, serif" }}>Menu</h3>
              <button onClick={() => setIsMobileMenuOpen(false)} style={{ background: "none", border: "none", color: TOKENS.muted, fontSize: 20, display: "flex" }}><X size={24} /></button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "24px 16px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, alignContent: "start", background: TOKENS.bgPage }}>
              {TABS.map((t) => {
                const { id, label, mobileLabel, Icon, action } = t;
                const active = tab === id && !selectedEmpId;
                return (
                  <button
                    key={id}
                    onClick={() => {
                      if (action) action();
                      else handleTabChange(id);
                      setIsMobileMenuOpen(false);
                    }}
                    style={{
                      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12,
                      padding: "20px 12px", borderRadius: 16, border: `1.5px solid ${active ? TOKENS.gold : TOKENS.border}`,
                      background: active ? `${TOKENS.gold}15` : "#fff",
                      color: active ? TOKENS.navyDeep : TOKENS.ink,
                      cursor: "pointer", boxShadow: "0 4px 12px rgba(0,0,0,0.03)"
                    }}
                  >
                    <Icon size={28} color={active ? TOKENS.gold : TOKENS.navySoft} />
                    <span style={{ textAlign: "center", fontSize: 13, fontWeight: 700, lineHeight: 1.2 }}>{mobileLabel || label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Mobile Password Change Modal */}
        {isPwModalOpen && (
          <div style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-start", paddingTop: 80, justifyContent: "center",
            zIndex: 1000, padding: 16
          }}>
            <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 400, padding: 24, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep, margin: 0 }}>
                  🔑 Change Password
                </h3>
                <button onClick={() => setIsPwModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: TOKENS.muted }}>✕</button>
              </div>

              {pwError && (
                <div style={{ padding: "8px 12px", background: "#fee2e2", border: "1px solid #ef4444", borderRadius: 8, color: "#991b1b", fontSize: 12, marginBottom: 12 }}>
                  {pwError}
                </div>
              )}

              <form onSubmit={handlePasswordSubmit}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 4 }}>Current Password</label>
                  <input
                    type="password"
                    value={currentPw}
                    onChange={(e) => setCurrentPw(e.target.value)}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
                    required
                  />
                </div>

                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 4 }}>New Password</label>
                  <input
                    type="password"
                    value={newPw}
                    onChange={(e) => setNewPw(e.target.value)}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
                    required
                  />
                </div>

                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 4 }}>Confirm New Password</label>
                  <input
                    type="password"
                    value={confirmPw}
                    onChange={(e) => setConfirmPw(e.target.value)}
                    style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
                    required
                  />
                </div>

                <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    onClick={() => setIsPwModalOpen(false)}
                    style={{ background: TOKENS.cream, color: TOKENS.ink, border: `1px solid ${TOKENS.border}`, borderRadius: 8, padding: "10px 16px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={pwLoading}
                    style={{ background: TOKENS.navyDeep, color: TOKENS.gold, border: "none", borderRadius: 8, padding: "10px 18px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                  >
                    {pwLoading ? "Updating…" : "Update Password"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── DESKTOP LAYOUT ─────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", height: "100vh", width: "100vw", overflow: "hidden", background: TOKENS.cream }}>
      {/* Desktop Sidebar Nav */}
      <div style={{
        width: 280,
        background: `linear-gradient(180deg, ${TOKENS.navyDeep}, ${TOKENS.navySoft})`,
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        borderRight: `1px solid ${TOKENS.border}15`,
        boxShadow: "4px 0 24px rgba(10,25,50,0.12)",
        flexShrink: 0,
      }}>
        <div style={{ padding: "28px 24px", borderBottom: `1px solid ${TOKENS.border}15`, flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src="/goldpe-logo.png" alt="GoldPE Logo" className="logo-glow" style={{ width: 38, height: 38, borderRadius: 10, objectFit: "contain", background: "#fff", padding: 2, boxShadow: `0 4px 12px ${TOKENS.gold}33` }} />
            <div>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: "#fff", lineHeight: 1.1 }}>GoldPE WIBIL</div>
              <div style={{ fontSize: 9.5, color: TOKENS.goldLight, fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase", marginTop: 2 }}>Manager Portal</div>
            </div>
          </div>
        </div>

        <div style={{ padding: "20px 16px", display: "flex", flexDirection: "column", gap: 4, flex: 1, overflowY: "auto" }}>
            {SIDEBAR_STRUCTURE.map((item) => {
              if (item.type === "tab") {
                const t = TABS.find(x => x.id === item.id);
                if (!t) return null;
                const { id, label, Icon, action } = t;
                const displayLabel = item.overrideLabel || label;
                const active = tab === id && !selectedEmpId;
                return (
                  <button key={id} onClick={() => action ? action() : handleTabChange(id)} style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "10px 14px", borderRadius: 10, border: "none", background: active ? `${TOKENS.gold}18` : "transparent", color: active ? TOKENS.gold : "#9FB0C9", fontFamily: "Inter, sans-serif", fontSize: 13.5, fontWeight: active ? 700 : 500, textAlign: "left", cursor: "pointer", transition: "all 0.15s ease", flexShrink: 0 }} className="sidebar-tab-btn">
                    <Icon size={18} color={active ? TOKENS.gold : "#8C9DB5"} />
                    <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayLabel}</span>
                    {active && <span style={{ width: 5, height: 5, borderRadius: "50%", background: TOKENS.gold, flexShrink: 0 }} />}
                  </button>
                );
              } else if (item.type === "group") {
                const isOpen = expandedGroups[item.id];
                const hasActiveChild = item.children.some(child => child.id === tab && !selectedEmpId);
                return (
                  <div key={item.id} style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <button onClick={() => toggleGroup(item.id)} style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "10px 14px", borderRadius: 10, border: "none", background: "transparent", color: hasActiveChild ? TOKENS.gold : "#C7D2E3", fontFamily: "Inter, sans-serif", fontSize: 13.5, fontWeight: 700, textAlign: "left", cursor: "pointer", transition: "all 0.15s ease", flexShrink: 0, marginTop: 4 }}>
                      <item.Icon size={18} color={hasActiveChild ? TOKENS.gold : "#8C9DB5"} />
                      <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textTransform: "uppercase", fontSize: 11, letterSpacing: 0.5 }}>{item.label}</span>
                      {isOpen ? <ChevronDown size={14} color="#8C9DB5"/> : <ChevronRight size={14} color="#8C9DB5"/>}
                    </button>
                    {isOpen && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, paddingLeft: 8 }}>
                        {item.children.map(childItem => {
                          const t = TABS.find(x => x.id === childItem.id);
                          if (!t) return null;
                          const { id, label, Icon, action } = t;
                          const displayLabel = childItem.overrideLabel || label;
                          const active = tab === id && !selectedEmpId;
                          return (
                            <button key={id} onClick={() => action ? action() : handleTabChange(id)} style={{ display: "flex", alignItems: "center", gap: 12, width: "100%", padding: "8px 14px", borderRadius: 10, border: "none", background: active ? `${TOKENS.gold}18` : "transparent", color: active ? TOKENS.gold : "#9FB0C9", fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: active ? 700 : 500, textAlign: "left", cursor: "pointer", transition: "all 0.15s ease", flexShrink: 0 }} className="sidebar-tab-btn">
                              <span style={{ width: 18, display: "flex", justifyContent: "center" }}>
                                <Icon size={16} color={active ? TOKENS.gold : "#8C9DB5"} />
                              </span>
                              <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{displayLabel}</span>
                              {active && <span style={{ width: 5, height: 5, borderRadius: "50%", background: TOKENS.gold, flexShrink: 0 }} />}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              return null;
            })}
          </div>

        <div style={{ padding: "20px 16px", borderTop: `1px solid ${TOKENS.border}15`, background: "rgba(10,21,45,0.25)", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#C7D2E3", fontSize: 12, marginBottom: 14, paddingLeft: 8 }}>
            <Clock size={14} color={TOKENS.gold} />
            <span>IST time: <b>{fmtTime(now)}</b></span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, paddingLeft: 4 }}>
            <div onClick={() => fileInputRef.current.click()} style={{ width: 40, height: 40, borderRadius: "50%", background: TOKENS.goldPale, border: `2.5px solid ${TOKENS.gold}`, display: "flex", alignItems: "center", justifyContent: "center", color: TOKENS.gold, fontSize: 15, fontWeight: 700, cursor: "pointer", overflow: "hidden" }} title="Click to upload profile photo">
              {managerPic ? <img src={managerPic} alt="Manager Avatar" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : "VR"}
            </div>
            <input type="file" accept="image/*" ref={fileInputRef} onChange={handlePicChange} style={{ display: "none" }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Vijay Rajagopal</div>
              <div style={{ fontSize: 11, color: "#8C9DB5", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>Manager -GoldPE</div>
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <button
              onClick={() => {
                setIsNotifOpen(true);
                markNotificationsRead();
              }}
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", padding: "6px 12px", borderRadius: 8, border: `1px solid ${TOKENS.gold}40`, background: `${TOKENS.gold}15`, color: TOKENS.gold, fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s ease", position: "relative" }}
            >
              <Bell size={14} /> Notifications
              {unreadNotifCount > 0 && (
                <span style={{ background: "#ef4444", color: "#fff", fontSize: 9, fontWeight: 800, borderRadius: 10, padding: "1px 6px", marginLeft: 4 }}>
                  {unreadNotifCount}
                </span>
              )}
            </button>
            <button 
              onClick={() => setIsPwModalOpen(true)} 
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", padding: "6px 12px", borderRadius: 8, border: `1px solid ${TOKENS.gold}40`, background: `${TOKENS.gold}15`, color: TOKENS.gold, fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s ease" }}
            >
              <KeyRound size={14} /> Change Password
            </button>
            <button 
              onClick={logout} 
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, width: "100%", padding: "6px 12px", borderRadius: 8, border: `1px solid rgba(255,255,255,0.15)`, background: "rgba(255,255,255,0.05)", color: "#E8896F", fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.15s ease" }} 
              className="sidebar-logout"
            >
              <LogOut size={14} /> Sign Out
            </button>
          </div>
        </div>
      </div>

      {/* Main Workspace */}
      <div style={{ flex: 1, minWidth: 0, height: "100vh", overflowY: "auto", padding: "20px 24px", background: TOKENS.bgPage }}>
        <div style={{ maxWidth: 1400, margin: "0 auto" }}>
          <div key={selectedEmpId || tab} className="screen-enter">
            {selectedEmpId ? (
              <EmployeeDetailScreen empId={selectedEmpId} onBack={() => setSelectedEmpId(null)} />
            ) : (
              <>
                {tab === "overview"      && <OverviewScreen onSelectEmp={(id) => setSelectedEmpId(id)} onNavigate={handleTabChange} />}
                {tab === "team"          && <TeamScreen onSelectEmp={(id) => setSelectedEmpId(id)} />}
                {tab === "reports"       && <ManagerReportsCalendarScreen onSelectEmp={(id) => setSelectedEmpId(id)} />}
                {tab === "reimbursement" && <ReimbursementApprovals />}
                {tab === "voucherPrinter" && <VoucherPrinterScreen />}
                {tab === "leaves"        && <ManagerLeaveScreen />}
                {tab === "lunch"         && <ManagerLunchScreen />}
                {tab === "incidents"     && <ManagerIncidentScreen />}
                {tab === "payroll"       && <ManagerPayrollScreen />}
                {tab === "projects"      && <ProjectsScreen onNavigate={handleTabChange} />}
                {tab === "pm_tasks"      && <TasksScreen />}
                {tab === "deliveries"    && <ManagerDeliveryScreen />}
                {tab === "team_alloc"    && <ManagerAllocationsScreen />}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Notifications Drawer Modal */}
      {isNotifOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-start", paddingTop: 80, justifyContent: "center",
          zIndex: 1000, padding: 16
        }}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, maxHeight: "80vh", display: "flex", flexDirection: "column", padding: 20, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, paddingBottom: 10, borderBottom: `1px solid ${TOKENS.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Bell size={18} color={TOKENS.navyDeep} />
                <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep, margin: 0 }}>
                  Notifications & Alerts
                </h3>
              </div>
              <button onClick={() => setIsNotifOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: TOKENS.muted }}>✕</button>
            </div>

            <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10 }}>
              {notifications.length === 0 ? (
                <div style={{ textAlign: "center", padding: 32, color: TOKENS.muted, fontSize: 13 }}>
                  🔔 No notifications yet.
                </div>
              ) : (
                notifications.map((n) => (
                  <div key={n.id} style={{
                    padding: 12, borderRadius: 10,
                    border: `1px solid ${n.is_read ? TOKENS.border : TOKENS.gold}`,
                    background: n.is_read ? "#FAFBFD" : `${TOKENS.gold}12`,
                    transition: "all 0.15s ease"
                  }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: TOKENS.navyDeep }}>{n.title}</span>
                      <span style={{ fontSize: 10, color: TOKENS.muted }}>
                        {n.created_at ? String(n.created_at).slice(0, 16).replace("T", " ") : ""}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: TOKENS.ink, lineHeight: 1.4 }}>{n.body}</div>
                  </div>
                ))
              )}
            </div>

            <div style={{ marginTop: 14, paddingTop: 10, borderTop: `1px solid ${TOKENS.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <button
                onClick={() => markNotificationsRead()}
                style={{ background: "none", border: "none", color: TOKENS.navyDeep, fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}
              >
                <CheckCheck size={14} /> Mark All as Read
              </button>
              <button
                onClick={() => setIsNotifOpen(false)}
                style={{ background: TOKENS.navyDeep, color: TOKENS.gold, border: "none", borderRadius: 8, padding: "8px 16px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Password Change Modal */}
      {isPwModalOpen && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-start", paddingTop: 80, justifyContent: "center",
          zIndex: 1000, padding: 16
        }}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 400, padding: 24, boxShadow: "0 10px 25px rgba(0,0,0,0.2)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <h3 style={{ fontFamily: "Fraunces, serif", fontSize: 18, fontWeight: 700, color: TOKENS.navyDeep, margin: 0 }}>
                🔑 Change Manager Password
              </h3>
              <button onClick={() => setIsPwModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 18, color: TOKENS.muted }}>✕</button>
            </div>

            {pwError && (
              <div style={{ padding: "8px 12px", background: "#fee2e2", border: "1px solid #ef4444", borderRadius: 8, color: "#991b1b", fontSize: 12, marginBottom: 12 }}>
                {pwError}
              </div>
            )}

            <form onSubmit={handlePasswordSubmit}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 4 }}>Current Password</label>
                <input
                  type="password"
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
                  required
                />
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 4 }}>New Password</label>
                <input
                  type="password"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
                  required
                />
              </div>

              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: TOKENS.muted, marginBottom: 4 }}>Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: 8, border: `1px solid ${TOKENS.border}`, fontSize: 13, outline: "none" }}
                  required
                />
              </div>

              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => setIsPwModalOpen(false)}
                  style={{ background: TOKENS.cream, color: TOKENS.ink, border: `1px solid ${TOKENS.border}`, borderRadius: 8, padding: "10px 16px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={pwLoading}
                  style={{ background: TOKENS.navyDeep, color: TOKENS.gold, border: "none", borderRadius: 8, padding: "10px 18px", fontWeight: 700, fontSize: 12, cursor: "pointer" }}
                >
                  {pwLoading ? "Updating…" : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
